import GolfService from './GolfService.js'
import { testRuleToBlock, validateResourceRule } from '../common/ReservationService.js'

const settings = window.wisenetwork || {};
const VIEW_MODES = {
    rows: 1,
    grid: 2
};
let defaultViewMode = VIEW_MODES.rows;

/*
if (!!localStorage['golfResevationViewMode']) {
    try {
        defaultViewMode = JSON.parse(localStorage['golfResevationViewMode']);
    } catch(e) {}
}
*/

export default {
    namespaced: true,
    state: {
        selectedViewMode: defaultViewMode,
        selectedPlayers: [], // ids of co-players
        allPlayers: [], // players from database
        playerAccessRights: [],
        accessRights: [],
        clubs: [],
        reservationPlayers: [], // separate from other player data; players linked to reservations
        golfReservations: [],
        defaultClubId: null,
        defaultHandicaps: {},
        selectedTeeTimes: [],
    },
    getters: {
        VIEW_MODES: (state) => VIEW_MODES,
        GolfService: () => GolfService,
        selectedViewMode: (state) => state.selectedViewMode,
        userPlayer: (state) => {
            if (!settings.user) {
                return { personId: null }
            }
            
            const personId = settings.user.personId;
            const player = state.allPlayers.filter(row => row.personId === personId)[0];
            
            if (!player) {
                return { personId: null }
            }
            
            return player;
        },
        isPlayerSelected: (state) => (player) => state.selectedPlayers.indexOf(player.personId) > -1,
        selectedPlayers: (state) => {
            return state.selectedPlayers.map((personId) => {
                return state.allPlayers.filter(row => row.personId === personId)[0]
            })
        },
        allPlayers: (state) => state.allPlayers,
        playerAccessRights: (state) => state.playerAccessRights,
        accessRights: (state) => state.accessRights,
        players: (state) => {
            const personId = !!settings.user ? settings.user.personId : null;
            const allButUser = state.allPlayers.filter((row) => row.personId !== personId);
            const selectedPlayers = state.selectedPlayers;
            
            return allButUser.filter(row => selectedPlayers.indexOf(row.personId) === -1);
        },
        allPlayerPrices: (state, getters, rootState, rootGetters) => {
            const resourceRules = rootGetters['reservations/resourceRules'];
            const activeReservation = rootGetters['calendar/activeReservation'];
            const adminMode = rootGetters['common/adminMode'];
            const { start, end } = activeReservation;
            
            return state.allPlayers.map(player => {
                const accessRights = state.playerAccessRights.filter(row => row.personId === player.personId);
                const categoryIds = accessRights.map(row => row.categoryId);
                const rules = resourceRules
                .filter(row => testRuleToBlock(row, { start, end }))
                .filter(row => 
                    activeReservation.resourceId === row.resourceId
                    && row.ruleName === 'pelioikeus' 
                    && categoryIds.indexOf(row.categoryId) > -1);
                let playerPrice = activeReservation.price;
                
                if (rules.length > 0) {
                    playerPrice = rules.reduce((acc, curr) => {
                        if (typeof curr.ruleValue !== 'object') return acc;

                        const { price: newPrice, percent: newPercent } = curr.ruleValue;
                        let price = acc;

                        if (typeof newPrice === 'number') {
                            price = newPrice < acc ? newPrice : acc;
                        }

                        if (typeof newPercent === 'number') {
                            const percent = newPercent / 100;
                            price = price - (price * percent);
                        }

                        return price;
                    }, playerPrice)
                }

                // detect visitor player
                if (!adminMode && accessRights.length === 0) {
                    // get the user's right for a new price for the visitor player
                    const userPlayer = getters['userPlayer'];
                    const userAccessRights = state.playerAccessRights.filter(row => row.personId === userPlayer.personId);
                    const userCategoryIds = userAccessRights.map(row => row.categoryId);
                    const visitorPriceRules = resourceRules.filter(row => 
                        row.ruleName === 'vierailijaHinta' 
                        && userCategoryIds.indexOf(row.categoryId) > -1);

                    if (visitorPriceRules.length > 0) {
                        playerPrice = visitorPriceRules.reduce((acc, curr) => {
                            if (typeof curr.ruleValue === 'number') {
                                return curr.ruleValue < acc ? curr.ruleValue : acc;
                            }

                            return acc;
                        }, playerPrice)
                    }
                }

                return {
                    personId: player.personId,
                    price: playerPrice
                }
            })
        },
        clubs: (state) => state.clubs,
        reservationPlayers: (state) => state.reservationPlayers,
        golfReservations: (state) => state.golfReservations.toSorted((a, b) => {
            return new Date(a.dateTimeStart) > new Date(b.dateTimeStart) ? -1 : 1
        }),
        defaultClubId: (state) => state.defaultClubId,
        defaultHandicaps: (state, getters, rootState, rootGetters) => {
            if (!Array.isArray(state.defaultHandicaps)) {
                return {};
            }
            return state.defaultHandicaps
                .filter(row => validateResourceRule(row, rootGetters['reservations/date']))
                .reduce((acc, curr) => {
                    acc[curr.resourceId] = curr.ruleValue.default;
                    return acc;
                }, {})
        },
        selectedTeeTimes: (state) => state.selectedTeeTimes.sort((a, b) => new Date(a.start) > new Date(b.start) ? 1 : -1),
        showStickyNotes(state) {
            if (state.selectedTeeTimes.length > 0) return true;

            return false;
        }
    },
    mutations: {
        toggleViewMode(state) {
            if (state.selectedViewMode === VIEW_MODES.rows) {
                state.selectedViewMode = VIEW_MODES.grid;
            } else {
                state.selectedViewMode = VIEW_MODES.rows;
            }
            
            localStorage['golfResevationViewMode'] = JSON.stringify(state.selectedViewMode);
        },
        setDefaultClubId(state, clubId) {
            state.defaultClubId = clubId;
        },
        setDefaultHandicaps(state, rows) {
            state.defaultHandicaps = rows;
        },
        setReservationPlayers(state, players) {
            state.reservationPlayers = players;
        },
        setSelectedPlayers(state, players) {
            state.selectedPlayers = players;
        },
        selectPlayer(state, personId) {
            if (state.selectedPlayers.indexOf(personId) === -1) {
                state.selectedPlayers.push(personId);
            }
        },
        deselectPlayer(state, personId) {
            const index = state.selectedPlayers.indexOf(personId);
            
            state.selectedPlayers.splice(index, 1);
        },
        addPlayers(state, players) {
            const personIds = state.allPlayers.map(row => row.personId);
            const newPlayers = players.filter(row => personIds.indexOf(row.personId) === -1);
            
            state.allPlayers = [
                ...state.allPlayers,
                ...newPlayers,
            ];
        },
        addPlayerAccessRights(state, rows) {
            const ids = state.playerAccessRights.map(row => `${ row.personId }-${ row.categoryId }`);
            const newRows = rows.filter(row => ids.indexOf(`${ row.personId }-${ row.categoryId }`) === -1);
            
            state.playerAccessRights = [
                ...state.playerAccessRights,
                ...newRows,
            ];
        },
        addAccessRights(state, rows) {
            const ids = state.accessRights.map(row => row.categoryId);
            const newRows = rows.filter(row => ids.indexOf(row.categoryId) === -1);
            
            state.accessRights = [
                ...state.accessRights,
                ...newRows,
            ];
        },
        addClubs(state, clubs) {
            const clubIds = state.clubs.map(row => row.clubId);
            const newClubs = clubs.filter(row => clubIds.indexOf(row.clubId) === -1);
            
            state.clubs = [
                ...state.clubs,
                ...newClubs,
            ];
        },
        setGolfReservations(state, rows) {            
            state.golfReservations = rows;
        },
        setSelectedTeeTimes(state, rows) {
            state.selectedTeeTimes = rows;
        }
    },
    actions: {
        getPlayerInformation({ commit }, query = {}) {
            return GolfService.getPlayers(query)
                .then((response) => {
                    const { data } = response;
                    
                    if (Array.isArray(data.rows)) {
                        commit('addPlayers', data.rows)
                    }

                    if (Array.isArray(data.accessRights)) {
                        commit('addPlayerAccessRights', data.accessRights)
                    }
                    
                    return response;
                });
        },
        getClubInformation({ commit }, query = {}) {
            return GolfService.getClubs(query)
                .then((response) => {
                    const { data } = response;
                    
                    if (Array.isArray(data.rows)) {
                        commit('addClubs', data.rows)
                    }
                    
                    return response;
                });
        },
        saveVisitorPlayer({ commit }, data) {
            return GolfService.saveVisitorPlayer(data)
                .then(response => {
                    const { data } = response;

                    if (data.success) {
                        commit('addPlayers', [ data.player ]);
                        //commit('selectPlayer', data.player.personId);
                    }

                    return response;
                })
        },
        getUserGolfReservations({ commit, rootGetters }, params = {}) {
            const EcomService = rootGetters['ecom/EcomService'];
            
            return EcomService.getUserGolfReservations(params)
                .then((response) => {
                    const { data } = response;

                    if (Array.isArray(data.rows)) {
                        commit('setGolfReservations', data.rows);
                    }
                })
        },
    }
}